import {
  type InvestmentAssetAllocation,
  type InvestmentBrokerageCompanionAccount,
  type InvestmentIRACompanionAccount,
  type InvestmentRothIRACompanionAccount,
} from '@egw/api'
import { Collapse, PieChart, type DefaultProps } from '@egw/components'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import cx from 'classnames'
import { type FC } from 'react'
import { twMerge } from 'tailwind-merge'
import { useFlags } from '@egw/feature-flags'
import { CompanionHolding } from '../companion-holding/companion-holding'

export interface OverviewApexHoldingsProps extends DefaultProps {
  assetAllocation: InvestmentAssetAllocation
  companionAccounts: (
    | InvestmentBrokerageCompanionAccount
    | InvestmentRothIRACompanionAccount
    | InvestmentIRACompanionAccount
  )[]
}

export const OverviewApexHoldings: FC<OverviewApexHoldingsProps> = ({
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
  assetAllocation,
  companionAccounts,
}) => {
  const { t } = useTranslation('features-investment')

  // Component base class names. Can be overriden by className property.
  const baseClass = cx('w-full')

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  const { flags } = useFlags<FeatureFlags>()

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <h3 className="typo-primary-xl-light text-text-meta mb-sm">
        {t('holdings.portfolio_allocation')}
      </h3>
      <div className="w-full flex flex-col mb-xl">
        <div className="flex items-center border-b border-gray-100 py-sm">
          <div className="flex items-center md:gap-lg gap-sm w-full">
            <PieChart
              segmentOne={assetAllocation.equities.percentage}
              segmentTwo={assetAllocation.fixedIncome.percentage}
              segmentThree={assetAllocation.cashEquivalent.percentage}
              segmentFour={assetAllocation.other.percentage}
              highlightedSegment={1}
              showPercentage={false}
              className="h-[80px]"
            />
            <div className="flex flex-col md:flex-row w-full items-center">
              <div className="flex-1 text-text-header text-left typo-primary-lg-semibold  w-full">
                {t('equity.title')}
              </div>
              <div className="flex-1 typo-primary-base text-left w-full">
                {t('common.percent_formatter_of_portfolio', { percentage: assetAllocation.equities.percentage })}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center border-b border-gray-100 py-sm">
          <div className="flex items-center md:gap-lg gap-sm w-full">
            <PieChart
              segmentOne={assetAllocation.equities.percentage}
              segmentTwo={assetAllocation.fixedIncome.percentage}
              segmentThree={assetAllocation.cashEquivalent.percentage}
              segmentFour={assetAllocation.other.percentage}
              highlightedSegment={2}
              showPercentage={false}
              className="h-[80px]"
            />
            <div className="flex flex-col md:flex-row w-full items-center">
              <p className="flex-1 text-text-header text-left typo-primary-lg-semibold  w-full">
                {t('fixed_income.title')}
              </p>
              <p className="flex-1 text-left w-full typo-primary-base">
                {t('common.percent_formatter_of_portfolio', { percentage: assetAllocation.fixedIncome.percentage })}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center border-b border-gray-100 py-sm">
          <div className="flex w-full items-center md:gap-lg gap-sm">
            <PieChart
              segmentOne={assetAllocation.equities.percentage}
              segmentTwo={assetAllocation.fixedIncome.percentage}
              segmentThree={assetAllocation.cashEquivalent.percentage}
              segmentFour={assetAllocation.other.percentage}
              highlightedSegment={3}
              showPercentage={false}
              className="h-[80px]"
            />
            <div className="flex flex-col md:flex-row w-full items-center">
              <div className="flex-1 text-text-header text-left typo-primary-lg-semibold  w-full">
                {t('risk_return.cash_equivalent')}
              </div>
              <div className="flex-1 typo-primary-base text-left w-full">
                {t('common.percent_formatter_of_portfolio', {
                  percentage: assetAllocation.cashEquivalent.percentage,
                })}
              </div>
            </div>
          </div>
        </div>
        {assetAllocation.other.percentage > 0 ? (
          <div className="flex items-center border-b border-gray-100 py-sm">
            <div className="flex w-full items-center md:gap-lg gap-sm">
              <PieChart
                segmentOne={assetAllocation.equities.percentage}
                segmentTwo={assetAllocation.fixedIncome.percentage}
                segmentThree={assetAllocation.cashEquivalent.percentage}
                segmentFour={assetAllocation.other.percentage}
                highlightedSegment={4}
                showPercentage={false}
                className="h-[80px]"
              />
              <div className="flex flex-col md:flex-row w-full items-center">
                <div className="flex-1 text-text-header text-left typo-primary-lg-semibold  w-full">
                  {t('risk_return.uncategorized')}
                </div>
                <div className="flex-1 typo-primary-base text-left w-full">
                  {t('common.percent_formatter_of_portfolio', {
                    percentage: assetAllocation.other.percentage,
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div>
        <p className="typo-primary-base-semibold text-text-meta capitalize">{t('holdings.other_holdings')}</p>
        <div className="flex flex-col">
          {
            // for each companion account render a collapse, and map over all the holdings within said companion account and render a CompanionHolding
            companionAccounts.map(({ __typename, accountNumber, holdings, alias, accountName }) => {
              return (
                <Collapse className="typo-primary-lg-semibold py-sm" title={accountName} key={accountNumber}>
                  {holdings.length > 0 ? (
                    holdings
                      .sort((a, b) => b.amount.amount - a.amount.amount)
                      .slice(0, 3)
                      .map((holding) => (
                        <CompanionHolding
                          key={holding.symbol}
                          amount={holding.amount}
                          symbol={holding.symbol}
                          name={holding.name}
                          // Go to All Holdings Screen HERE not the Holding detail screen
                          href={WEALTH_ROUTES.INVESTMENTS_COMPANION_ACCOUNT_HOLDINGS.path.replace(
                            ':alias',
                            alias.toString(),
                          )}
                        />
                      ))
                  ) : (
                    <p className="text-text-header typo-primary-xl-light p-lg text-center">
                      {t('holdings.no_holdings')}
                    </p>
                  )}
                </Collapse>
              )
            })
          }
        </div>
      </div>
    </div>
  )
}
